import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useSurvey }    from '../context/SurveyContext';
import { useSurveyApi } from '../hooks/useSurveyApi';
import { dobSchema }    from '../schemas/survey.schema';

import PageShell      from '../components/layout/PageShell';
import FormCard       from '../components/layout/FormCard';
import ProgressBar    from '../components/survey/ProgressBar';
import WelcomeHeader  from '../components/survey/WelcomeHeader';
import DatePicker     from '../components/ui/DatePicker';
import Button         from '../components/ui/Button';
import ErrorBanner    from '../components/ui/ErrorBanner';
import { debugLog }   from '../utils/logger';

/**
 * ValidatePage  (/validate?pid=<token>)
 *
 * Flow:
 *   1. On mount → POST /survey/verify-token with pid from URL
 *      → stores sessionToken + firstName + practiceName in context
 *   2. Patient enters DOB (text or calendar)
 *   3. On submit → POST /survey/verify-dob
 *      → stores patient data in context
 *      → navigates to /preferences
 */
export default function ValidatePage() {
    const [searchParams]                = useSearchParams();
    const navigate                      = useNavigate();
    const { practiceName } = useSurvey();
    const { verifyToken, verifyDob, loading, error, clearError } = useSurveyApi();

    const [firstName,     setFirstName    ] = useState(null);
    const [tokenVerified, setTokenVerified] = useState(false);
    const [tokenError,    setTokenError   ] = useState(null);
    const [tokenLoading,  setTokenLoading ] = useState(true);

    const { control, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(dobSchema)
    });

    useEffect(() => {
        debugLog('ui_validate_page_mounted');
    }, []);

    // ── Step 1: verify token on mount ──────────────────────────────────────
    useEffect(() => {
        const pid = searchParams.get('pid');
        if (!pid) {
            debugLog('ui_validate_token_missing');
            setTokenError({ code: 'INVALID_TOKEN' });
            setTokenLoading(false);
            return;
        }

        verifyToken(pid)
            .then(({ firstName: fn }) => {
                setFirstName(fn);
                setTokenVerified(true);
                debugLog('ui_validate_token_verified');
            })
            .catch((err) => {
                debugLog('ui_validate_token_failed', { code: err.code, status: err.status });
                setTokenError(err);
            })
            .finally(() => setTokenLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Step 2: verify DOB on form submit ──────────────────────────────────
    const onSubmit = useCallback(async ({ dob }) => {
        clearError();
        debugLog('ui_validate_dob_submit_started');
        try {
            await verifyDob(dob);
            debugLog('ui_validate_dob_submit_succeeded');
            navigate('/preferences');
        } catch {
            debugLog('ui_validate_dob_submit_failed');
            // error is set in useSurveyApi — ErrorBanner handles display
        }
    }, [verifyDob, navigate, clearError]);

    // ── Render: loading ────────────────────────────────────────────────────
    if (tokenLoading) {
        return (
            <PageShell practiceName={null}>
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2ECC8E" strokeWidth="2" aria-label="Loading">
                        <circle cx="12" cy="12" r="10" strokeOpacity="0.2" />
                        <path d="M12 2a10 10 0 0 1 10 10" />
                    </svg>
                    <p className="text-sm" style={{ color: '#6b8fa8' }}>Verifying your link…</p>
                </div>
            </PageShell>
        );
    }

    // ── Render: token error ────────────────────────────────────────────────
    if (tokenError) {
        return (
            <PageShell practiceName={practiceName}>
                <FormCard>
                    <ErrorBanner error={tokenError} />
                    <p className="text-sm text-center" style={{ color: '#6b8fa8' }}>
                        If the problem persists, please contact the OPOS Support team at{' '}
                        <a href="tel:+16506919500" style={{ color: '#1B2F7E', fontWeight: 600, textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                            (650) 694-9500
                        </a>
                    </p>
                </FormCard>
            </PageShell>
        );
    }

    // ── Render: main form ──────────────────────────────────────────────────
    return (
        <PageShell practiceName={practiceName}>
            <ProgressBar step={1} />

            <FormCard>
                <WelcomeHeader firstName={firstName} />

                <ErrorBanner error={error} onDismiss={clearError} />

                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <label className="block text-xs font-semibold mb-2" style={{ color: '#1B2F7E' }}>
                        Date of birth
                    </label>

                    <Controller
                        name="dob"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                            <DatePicker
                                value={field.value}
                                onChange={field.onChange}
                                error={errors.dob?.message}
                            />
                        )}
                    />

                    <Button type="submit" loading={loading} disabled={!tokenVerified}>
                        Continue
                    </Button>
                </form>
            </FormCard>

        </PageShell>
    );
}
